// src/redux/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../utils/axiosInstance";
import { jwtDecode } from "jwt-decode"; // Ensure you import jwtDecode for decoding JWT tokens
import { encryptPayload } from "../utils/crypto";

const accessToken = sessionStorage.getItem("accessToken");
const refreshToken = sessionStorage.getItem("refreshToken");

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const encryptedPayload = encryptPayload(credentials); // 🔐 encrypt the login credentials
      const response = await axiosInstance.post("/api/auth/login", {
        payload: encryptedPayload,
      });
      const token = response.data.token;
      const refreshToken = response.data.refreshToken;

      // Decode token to get user info and role
      const decodedToken = jwtDecode(token);

      sessionStorage.setItem("accessToken", token);
      sessionStorage.setItem("refreshToken", refreshToken);

      // Return decoded data along with the token
      return {
        token,
        refreshToken,
        user: response.data.user,
        role: decodedToken.role,
        userId: decodedToken.userId,
      };
    } catch (err) {
      return rejectWithValue("Login failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    role: null,
    token: accessToken || null,
    refreshToken: refreshToken || null,
    isAuthenticated: !!accessToken,
    error: null,
    userId: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.role = null;
      state.token = null;
      state.refreshToken = null;
      state.userId = null;
      state.isAuthenticated = false;
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("refreshToken");
    },
    setUser: (state, action) => {
      state.user = action.payload.user;
      state.role = action.payload.role;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
    },
    setAccessToken: (state, action) => {
      state.token = action.payload;
      sessionStorage.setItem("accessToken", action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.error = null;
        state.userId = action.payload.userId;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { logout, setUser, setAccessToken } = authSlice.actions;
export default authSlice.reducer;
